/**
 * DamageMaterialProfile objects. Brick is the shipping signature.
 * Concrete / stone are stubs — same shape so they are populatable later
 * with their own research rounds. See `notes` on each stub and
 * docs/damage-system.md.
 */
import {
  CLAY_DEEP,
  CLAY_SALMON,
  MORTAR,
  MORTAR_CRUST,
  SOOT,
  STAIN_GREY,
  TERRACOTTA,
  TERRACOTTA_CHUNK,
} from './palette';
import type { DamageMaterialProfile, MaterialId, WeaponCrackSpec } from './types';

/** Copied (not imported) from the particle-lab thin-wall gate. */
export const THIN_WALL_MAX_DEPTH = 0.28;

const BRICK_CANNON_CRACK: WeaponCrackSpec = {
  mode: 'joint-graph',
  countK: 1,
  lengthK: 1,
  minCount: 3,
  maxCount: 6,
  stairBias: 8.5,
  branchChance: 0.22,
};

const BRICK_BULLET_CRACK: WeaponCrackSpec = {
  mode: 'through-unit',
  countK: 0,
  lengthK: 0,
  minCount: 0,
  maxCount: 0,
  stairBias: 0,
  branchChance: 0,
};

const BRICK_MISSILE_CRACK: WeaponCrackSpec = {
  mode: 'joint-graph',
  countK: 1.55,
  lengthK: 0.48,
  minCount: 5,
  maxCount: 9,
  stairBias: 8.5,
  branchChance: 0.28,
};

export const BRICK_PROFILE: DamageMaterialProfile = {
  id: 'brick',
  label: 'Brick',
  isStub: false,
  notes: 'Joint-routed stair-step masonry. Fort Pulaski / Sumter terminal-ballistics research.',
  atlasSet: 'brick-spall-hair',
  crackMode: 'joint-graph',
  coreK: 1,
  rimK: 1,
  spallK: 1,
  crackCountK: 1,
  crackLengthK: 1,
  weaponCrack: {
    bullet: BRICK_BULLET_CRACK,
    cannon: BRICK_CANNON_CRACK,
    missile: BRICK_MISSILE_CRACK,
  },
  rimPushIn: { lo: 0.008, hi: 0.022, missileLo: 0.004, missileHi: 0.01 },
  radii: {
    core: 0.18,
    rim: 0.36,
    spall: 0.7,
    missileCoreMul: 0.55,
    missileRimMul: 1.18,
    missileSpall: 0.72,
    missileSpallMul: 2.0,
  },
  spallProb: { cannon: 0.72, missile: 0.92 },
  integrity: { pock: 0.06, spall: 0.09, crack: 0.15, rim: 0.32 },
  pockEscalate: 3,
  bulletRemoveEnergy: 3,
  backFaceMaxDepth: THIN_WALL_MAX_DEPTH,
  backSpallScale: 0.52,
  backFaceBlastMin: 0.5,
  stain: { minLen: 0.11, lenRange: 0.34, minWidth: 0.01, widthRange: 0.022, visMin: 0.06 },
  film: { radiusK: 0.95 },
  scorch: { radiusK: 1.05 },
  crackVis: { haloPitches: 2.85, corePitches: 0.55 },
  debris: {
    fraction: 0.55,
    cap: 48,
    alwaysClayHeads: true,
    stretcherClayChance: 0.55,
    backStretcherClayChance: 0.3,
  },
  palette: {
    terracotta: TERRACOTTA,
    terracottaChunk: TERRACOTTA_CHUNK,
    claySalmon: CLAY_SALMON,
    clayDeep: CLAY_DEEP,
    mortar: MORTAR,
    mortarCrust: MORTAR_CRUST,
    stainGrey: STAIN_GREY,
    soot: SOOT,
  },
};

/**
 * TODO stub — concrete full signature is a later research round.
 * Intended: monolithic (no joint graph), radial frozen cracks, cone spall.
 * Research: plans/demolition-materials-reference.md, plans/damage-lab-plan.md.
 */
export const CONCRETE_PROFILE: DamageMaterialProfile = {
  ...BRICK_PROFILE,
  id: 'concrete',
  label: 'Concrete',
  isStub: true,
  notes:
    'TODO: monolithic radial frozen cracks, no joint graph, cone spall. Not a brick reskin — populate from demolition-materials-reference.md in a later round.',
  atlasSet: 'todo-concrete',
  crackMode: 'radial',
  weaponCrack: {
    bullet: { ...BRICK_BULLET_CRACK, mode: 'through-unit' },
    cannon: { ...BRICK_CANNON_CRACK, mode: 'radial', stairBias: 0, countK: 0, lengthK: 0 },
    missile: { ...BRICK_MISSILE_CRACK, mode: 'radial', stairBias: 0, countK: 0, lengthK: 0 },
  },
  spallProb: { cannon: 0, missile: 0 },
  stain: { minLen: 0, lenRange: 0, minWidth: 0, widthRange: 0, visMin: 1 },
  debris: {
    fraction: 0,
    cap: 0,
    alwaysClayHeads: false,
    stretcherClayChance: 0,
    backStretcherClayChance: 0,
  },
};

/**
 * TODO stub — stone full signature is a later research round.
 * Intended: block-quantized cracks, sharp spall plates, minimal dust film.
 * Research: plans/demolition-materials-reference.md, plans/damage-lab-plan.md.
 */
export const STONE_PROFILE: DamageMaterialProfile = {
  ...BRICK_PROFILE,
  id: 'stone',
  label: 'Stone',
  isStub: true,
  notes:
    'TODO: block-quantized sharp spall plates, minimal dust film. Not a brick reskin — populate from demolition-materials-reference.md in a later round.',
  atlasSet: 'todo-stone',
  crackMode: 'joint-graph',
  film: { radiusK: 0 },
  stain: { minLen: 0, lenRange: 0, minWidth: 0, widthRange: 0, visMin: 1 },
  debris: {
    fraction: 0,
    cap: 0,
    alwaysClayHeads: false,
    stretcherClayChance: 0,
    backStretcherClayChance: 0,
  },
};

export const DAMAGE_PROFILES: readonly DamageMaterialProfile[] = [
  BRICK_PROFILE,
  CONCRETE_PROFILE,
  STONE_PROFILE,
];

export function getProfile(id: MaterialId): DamageMaterialProfile {
  if (id === 'concrete') return CONCRETE_PROFILE;
  if (id === 'stone') return STONE_PROFILE;
  return BRICK_PROFILE;
}

const REQUIRED_KEYS: (keyof DamageMaterialProfile)[] = [
  'id',
  'label',
  'isStub',
  'notes',
  'atlasSet',
  'crackMode',
  'coreK',
  'rimK',
  'spallK',
  'crackCountK',
  'crackLengthK',
  'weaponCrack',
  'rimPushIn',
  'radii',
  'spallProb',
  'integrity',
  'pockEscalate',
  'bulletRemoveEnergy',
  'backFaceMaxDepth',
  'backSpallScale',
  'backFaceBlastMin',
  'stain',
  'film',
  'scorch',
  'crackVis',
  'debris',
  'palette',
];

/** True when every profile field is present and finite (stubs may be zeroed). */
export function profileFieldsPresent(p: DamageMaterialProfile): string[] {
  const missing: string[] = [];
  for (const k of REQUIRED_KEYS) {
    if (p[k] === undefined || p[k] === null) missing.push(String(k));
  }
  const weapons: Array<'bullet' | 'cannon' | 'missile'> = ['bullet', 'cannon', 'missile'];
  for (const w of weapons) {
    const wc = p.weaponCrack[w];
    if (!wc || !wc.mode) missing.push(`weaponCrack.${w}`);
  }
  const pal = p.palette;
  for (const key of ['terracotta', 'claySalmon', 'clayDeep', 'mortar', 'stainGrey', 'soot'] as const) {
    const rgb = pal[key];
    if (!rgb || rgb.length !== 3) missing.push(`palette.${key}`);
  }
  return missing;
}

export function allowsBackFace(
  profile: DamageMaterialProfile,
  wallDepth: number,
  blastFraction: number,
): boolean {
  if (wallDepth > profile.backFaceMaxDepth) return false;
  return blastFraction > profile.backFaceBlastMin;
}
